/**
 * Shared classes for the expert specialty tag (renders on four surfaces).
 *
 * A border now means "pressable" on any card showing an expert — the publications/trials
 * chips and the Reference Champion badge both carry one for that reason. The specialty tag
 * carried one too but does nothing, so it's dropped rather than darkened: its old
 * `border-info-300` only reached 1.86:1 against SC 1.4.11's 3:1, and a label's edge was never
 * really a component boundary to begin with.
 *
 * `rounded-md` because the `Badge` base's `rounded-sm` (2px) was the only place on the card
 * using that corner radius.
 */
export const SPECIALTY_BADGE_CLASS = 'rounded-md border-0';

/**
 * Type styles for the specialty tag's label, kept beside the shell so the two can't drift.
 * Only what differs from the `Badge` text base: `info-800` overrides the action's lighter
 * `info-600`.
 */
export const SPECIALTY_BADGE_TEXT_CLASS = 'text-xs text-info-800';

/**
 * A metric's trailing mark, one step above its label size — matched exactly, an emoji's
 * internal padding reads as shrunken next to the label's cap height.
 *
 * Only the badge reads this today. The response-time flame stays at label size because it's
 * baked into the server-owned label string rather than its own field; fixing that means
 * giving that endpoint a separate `mark` field, mirroring how the badge's payload already
 * splits `label` and `emoji`.
 */
export const METRIC_MARK_SIZE: Record<'text-xs' | 'text-sm', string> = {
  'text-xs': 'text-sm',
  'text-sm': 'text-sm md:text-base',
};

/**
 * The label size a metric actually renders at, which below `md` is one step down from what the
 * host asked for.
 *
 * The two metrics cannot share a line on a phone at `text-sm`. The widest response-time label
 * measures 135px, the badge with its mark 156px, and the gap between them 20px, which is 311px
 * of content against roughly 288px of usable width at 360dp and 303pt on the narrowest iPhone,
 * so it wrapped on both. Closing the gap entirely, or dropping the mark, each recover about
 * 20px and still leave it short; the type size is the only lever that moves both metrics at
 * once. At `text-xs` the same worst case is 270px.
 *
 * Headroom rather than a snug fit on purpose: the response-time label is server-owned copy, so
 * its width is a config decision that can change without a deploy.
 *
 * `text-xs` hosts are unchanged. They already fit, and stepping them down again would put the
 * directory card below the type floor.
 */
export const METRIC_LABEL_SIZE: Record<'text-xs' | 'text-sm', string> = {
  'text-xs': 'text-xs',
  'text-sm': 'text-xs md:text-sm',
};
